import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuthStore } from '../../store/authStore';
import { useCreditCosts } from '../../hooks/useCreditCosts';
import { useQuery, useMutation } from '@tanstack/react-query';
import api from '../../services/api';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import toast from 'react-hot-toast';
import {
  SparklesIcon,
  LinkIcon,
  TableCellsIcon,
  CircleStackIcon,
  PaperAirplaneIcon,
  CheckCircleIcon,
  ChatBubbleLeftRightIcon,
  PlusIcon,
  TrashIcon,
  ChevronDownIcon,
  XMarkIcon,
  LightBulbIcon,
  BoltIcon
} from '@heroicons/react/24/outline';

// Suggestion chips for AI quick actions
const SUGGESTION_CHIPS = [
  { label: 'What can I do with my data?', icon: LightBulbIcon },
  { label: 'Find connections between my tables', icon: LinkIcon },
  { label: 'Set up everything automatically', icon: BoltIcon },
  { label: 'Create a view to combine tables', icon: TableCellsIcon }
];

// Relationship type display names
const RELATIONSHIP_TYPES = {
  one_to_one: 'One to One',
  one_to_many: 'One to Many',
  many_to_many: 'Many to Many'
};

// Confidence level colors
const getConfidenceColor = (confidence) => {
  if (confidence >= 0.9) return 'bg-green-500';
  if (confidence >= 0.7) return 'bg-yellow-500';
  return 'bg-orange-500';
};

const RelationshipEditor = () => {
  const { user, getCurrentCompanyId, getCurrentCompany, isSuperUser } = useAuthStore();
  const companyId = getCurrentCompanyId();
  const currentCompany = getCurrentCompany();
  const { formatCost } = useCreditCosts();
  const messagesEndRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [aiDetecting, setAiDetecting] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [relationships, setRelationships] = useState([]);
  const [views, setViews] = useState([]);
  const [dataSources, setDataSources] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingRelationship, setEditingRelationship] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showCustomJoinModal, setShowCustomJoinModal] = useState(false);
  const [showUnionViewModal, setShowUnionViewModal] = useState(false);
  const [showEditViewModal, setShowEditViewModal] = useState(false);
  const [editingView, setEditingView] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [selectedRelationships, setSelectedRelationships] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Pending review state — previously deleted relationships that AI re-detected
  const [showPendingReviewModal, setShowPendingReviewModal] = useState(false);
  const [pendingReviewItems, setPendingReviewItems] = useState([]);
  const [pendingSelections, setPendingSelections] = useState({}); // { index: 'accept' | 'reject' }
  const [resolvingPending, setResolvingPending] = useState(false);
  
  // Action bar dropdown state
  const [openDropdown, setOpenDropdown] = useState(null); // 'relationship' | 'view' | null
  const relationshipDropdownRef = useRef(null);
  const viewDropdownRef = useRef(null);

  // AI Assistant state
  const [showAIPanel, setShowAIPanel] = useState(false);
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [isTyping, setIsTyping] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  
  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        relationshipDropdownRef.current && !relationshipDropdownRef.current.contains(e.target) &&
        viewDropdownRef.current && !viewDropdownRef.current.contains(e.target)
      ) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Check if company is selected (for Super/Managing Admins)
  const noCompanySelected = isSuperUser() && !companyId;
  
  // Fetch conversation history for Data Studio
  const { data: conversationsData, refetch: refetchConversations } = useQuery({
    queryKey: ['data-studio-conversations', companyId],
    queryFn: async () => {
      try {
        const response = await api.get('/data-studio/conversations', { params: { companyId } });
        return response.data;
      } catch (err) {
        // Silently handle 403/404 - just means no conversations yet
        if (err.response?.status === 403 || err.response?.status === 404) {
          return { data: [] };
        }
        throw err;
      }
    },
    enabled: !!companyId,
    retry: false // Don't retry on permission errors
  });
  
  // AI chat mutation
  const chatMutation = useMutation({
    mutationFn: async (userMessage) => {
      const response = await api.post('/data-studio/ai-assist', {
        message: userMessage,
        conversationId,
        companyId
      }, { timeout: 120000 });
      return response.data;
    },
    onSuccess: (data) => {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: data.response,
        toolCalls: data.toolCalls
      }]);
      setConversationId(data.conversationId);
      setIsTyping(false);
      
      // Refresh data if tools were called
      if (data.toolCalls?.length > 0) {
        fetchData();
      }
      refetchConversations();
    },
    onError: (error) => {
      setIsTyping(false);
      toast.error(error.response?.data?.error || 'Failed to get AI response');
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.',
        isError: true
      }]);
    }
  });
  
  // Load a conversation
  const loadConversation = async (convId) => {
    try {
      const response = await api.get(`/data-studio/conversations/${convId}`);
      const conv = response.data.data;
      setConversationId(convId);
      setMessages(conv.messages?.map(m => ({
        role: m.role,
        content: m.content,
        toolCalls: m.toolCalls
      })) || []);
      setShowHistory(false);
    } catch (error) {
      toast.error('Failed to load conversation');
    }
  };
  
  // Delete a conversation
  const deleteConversation = async (convId) => {
    if (!window.confirm('Delete this conversation?')) return;
    try {
      await api.delete(`/data-studio/conversations/${convId}`);
      refetchConversations();
      if (conversationId === convId) {
        startNewChat();
      }
      toast.success('Conversation deleted');
    } catch (error) {
      toast.error('Failed to delete conversation');
    }
  };
  
  // Start a new chat
  const startNewChat = () => {
    setConversationId(null);
    setMessages([]);
    setShowHistory(false);
  };
  
  // Handle sending a message
  const handleSendMessage = useCallback((msg) => {
    const messageText = msg || message.trim();
    if (!messageText) return;
    
    setMessages(prev => [...prev, { role: 'user', content: messageText }]);
    setMessage('');
    setIsTyping(true);
    chatMutation.mutate(messageText);
  }, [message, chatMutation]);
  
  // Handle chip click
  const handleChipClick = (chipLabel) => {
    handleSendMessage(chipLabel);
  };
  
  // Handle keyboard submit
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };
  
  // Scroll to bottom of messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  
  // Close history dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showHistory && !e.target.closest('.history-dropdown')) {
        setShowHistory(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showHistory]);

  // Fetch relationships and data sources
  const fetchData = useCallback(async () => {
    if (!companyId) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      setError(null); // Clear any previous errors
      const [relResponse, dsResponse] = await Promise.all([
        api.get('/relationships', { params: { companyId } }),
        api.get('/data-sources', { params: { companyId } })
      ]);
      
      setRelationships(relResponse.data.relationships || []);
      setViews(relResponse.data.views || []);
      // Data sources API returns paginated response with 'data' property
      setDataSources(dsResponse.data.data || dsResponse.data.dataSources || []);
    } catch (err) {
      // Only show error for actual API failures that users need to know about
      const status = err.response?.status;
      
      // Don't show error for these cases:
      // - 404: No data found yet (empty state)
      // - 403: Permission issues are usually handled by the "no company selected" UI
      if (status === 404 || status === 403) {
        // Silently ignore - the UI will show appropriate empty states
      } else if (status >= 500) {
        setError('Server error. Please try again later.');
      } else if (!err.response) {
        setError('Unable to connect. Please check your internet connection.');
      } else {
        // For other errors, show a generic message
        setError('Unable to load data. Please refresh the page.');
      }
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Run AI-powered detection
  const handleAIDetect = async () => {
    try {
      setAiDetecting(true);
      setError(null);
      const response = await api.post('/relationships/detect-ai', { companyId }, { timeout: 120000 }); // 2 minutes for AI
      const { detected, added, skippedExisting, pendingReview, summary } = response.data;
      const summaryText = summary ? `\n${summary}` : '';
      let statusMsg = `AI analyzed your tables and found ${detected} potential relationship${detected !== 1 ? 's' : ''}.`;
      if (added > 0) statusMsg += ` Added ${added} new one${added !== 1 ? 's' : ''}.`;
      if (skippedExisting > 0) statusMsg += ` ${skippedExisting} already existed.`;
      if (detected === 0 && (!pendingReview || pendingReview.length === 0)) {
        statusMsg = 'AI analysis complete — no new relationships found.';
      }
      
      // If there are previously-deleted relationships the AI re-detected, show review modal
      if (pendingReview && pendingReview.length > 0) {
        statusMsg += ` ${pendingReview.length} previously removed relationship${pendingReview.length !== 1 ? 's were' : ' was'} re-detected — please review.`;
        setPendingReviewItems(pendingReview);
        setPendingSelections({}); // Reset selections
        setShowPendingReviewModal(true);
      }
      
      setSuccess(`${statusMsg}${summaryText}`);
      await fetchData();
    } catch (err) {
      setError(err.response?.data?.details || 'AI-powered detection failed');
      console.error(err);
    } finally {
      setAiDetecting(false);
    }
  };

  // Resolve pending review items (accept/reject previously deleted relationships)
  const handleResolvePending = async () => {
    try {
      setResolvingPending(true);
      setError(null);
      
      const accepted = [];
      const rejected = [];
      
      pendingReviewItems.forEach((item, index) => {
        if (pendingSelections[index] === 'accept') {
          accepted.push(item);
        } else {
          // Default to reject if not explicitly accepted
          rejected.push(item);
        }
      });
      
      await api.post('/relationships/resolve-pending', { companyId, accepted, rejected });
      
      const addedCount = accepted.length;
      const rejectedCount = rejected.length;
      let msg = '';
      if (addedCount > 0) msg += `Re-added ${addedCount} relationship${addedCount !== 1 ? 's' : ''}.`;
      if (rejectedCount > 0) msg += `${msg ? ' ' : ''}Kept ${rejectedCount} dismissed.`;
      
      setSuccess(msg);
      setShowPendingReviewModal(false);
      setPendingReviewItems([]);
      setPendingSelections({});
      await fetchData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to resolve pending relationships');
      console.error(err);
    } finally {
      setResolvingPending(false);
    }
  };

  // Delete relationship
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this relationship?')) return;
    
    try {
      await api.delete(`/relationships/${id}`, { params: { companyId } });
      setRelationships(prev => prev.filter(r => r.id !== id));
      setSuccess('Relationship deleted');
    } catch (err) {
      setError('Failed to delete relationship');
      console.error(err);
    }
  };

  // Toggle relationship active status
  const handleToggleActive = async (id, isActive) => {
    try {
      await api.put(`/relationships/${id}`, { isActive: !isActive, companyId });
      setRelationships(prev => prev.map(r => 
        r.id === id ? { ...r, isActive: !isActive } : r
      ));
    } catch (err) {
      setError('Failed to update relationship');
      console.error(err);
    }
  };

  // Open edit modal
  const handleEdit = (relationship) => {
    setEditingRelationship(relationship);
    setShowEditModal(true);
  };

  // Update relationship
  const handleUpdate = async (id, data) => {
    try {
      await api.put(`/relationships/${id}`, { ...data, companyId });
      setRelationships(prev => prev.map(r => 
        r.id === id ? { ...r, ...data } : r
      ));
      setSuccess('Relationship updated');
      setShowEditModal(false);
      setEditingRelationship(null);
    } catch (err) {
      setError('Failed to update relationship');
      console.error(err);
    }
  };

  // Preview a view
  const handlePreview = async (viewName) => {
    try {
      setPreviewing(true);
      setError(null);
      const response = await api.post(`/relationships/views/${encodeURIComponent(viewName)}/preview`, { 
        limit: 100,
        companyId 
      });
      setPreviewData({
        viewName,
        ...response.data
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to preview view');
      console.error(err);
    } finally {
      setPreviewing(false);
    }
  };

  // Deploy a view
  const handleDeploy = async (viewName) => {
    try {
      setGenerating(true);
      setError(null);
      const response = await api.post(`/relationships/views/${encodeURIComponent(viewName)}/deploy`, { companyId });
      
      if (response.data.deployed) {
        setSuccess(`View "${viewName}" deployed to BigQuery successfully!`);
      } else {
        setError(response.data.error || 'Deployment failed');
      }
      await fetchData();
    } catch (err) {
      setError(err.response?.data?.error || 'View deployment failed');
      console.error(err);
    } finally {
      setGenerating(false);
    }
  };

  // Toggle relationship selection for wide view
  const toggleRelationshipSelection = (id) => {
    setSelectedRelationships(prev => 
      prev.includes(id) ? prev.filter(r => r !== id) : [...prev, id]
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Show message if no company selected (for super admins)
  if (noCompanySelected) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-6 text-center">
          <svg className="mx-auto h-12 w-12 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 className="mt-4 text-lg font-semibold text-amber-800 dark:text-amber-300">No Company Selected</h3>
          <p className="mt-2 text-amber-700 dark:text-amber-400">
            Please select a company from the company selector to view and manage relationships.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Compact Gradient Header */}
      <div className="bg-blue-600 dark:bg-blue-900">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                <SparklesIcon className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                  Data Studio
                  {formatCost('data_studio_build_step') && (
                    <span className="text-sm font-normal text-white/70">
                      {formatCost('data_studio_build_step')} per build step
                    </span>
                  )}
                </h1>
                <p className="text-white/80 text-sm">
                  Connect, transform, and prepare your data for analysis
                  {currentCompany && (
                    <span className="ml-2 text-white/60">
                      • {currentCompany.name || currentCompany}
                    </span>
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="max-w-7xl mx-auto px-6 -mt-4">
        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <CompactStatCard
            label="Data Sources"
            value={dataSources.length}
            icon={CircleStackIcon}
            color="blue"
            loading={loading}
          />
          <CompactStatCard
            label="Relationships"
            value={relationships.length}
            icon={LinkIcon}
            color="green"
            loading={loading}
          />
          <CompactStatCard
            label="Views"
            value={views.length}
            icon={TableCellsIcon}
            color="purple"
            loading={loading}
          />
        </div>

        {/* Alerts */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400">
          {error}
          <button onClick={() => setError(null)} className="float-right">&times;</button>
        </div>
      )}
      {success && (
        <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-green-700 dark:text-green-400">
          {success}
          <button onClick={() => setSuccess(null)} className="float-right">&times;</button>
        </div>
      )}

      {/* Actions Bar */}
      <div className="mb-6 flex items-center gap-3">
        {/* Add Relationship Dropdown */}
        <div className="relative" ref={relationshipDropdownRef}>
          <button
            onClick={() => setOpenDropdown(openDropdown === 'relationship' ? null : 'relationship')}
            className="px-4 py-2.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg flex items-center gap-2 transition-colors shadow-sm"
          >
            <LinkIcon className="w-4 h-4" />
            <span className="font-medium">Add Relationship</span>
            <ChevronDownIcon className={`w-4 h-4 transition-transform ${openDropdown === 'relationship' ? 'rotate-180' : ''}`} />
          </button>
          {openDropdown === 'relationship' && (
            <div className="absolute left-0 top-full mt-1 w-64 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-30 py-1">
              <button
                onClick={() => { handleAIDetect(); setOpenDropdown(null); }}
                disabled={aiDetecting}
                className="w-full px-4 py-2.5 text-left hover:bg-purple-50 dark:hover:bg-purple-900/20 disabled:opacity-50 flex items-center gap-3 transition-colors"
              >
                {aiDetecting ? (
                  <span className="animate-spin h-4 w-4 border-2 border-purple-500 border-t-transparent rounded-full flex-shrink-0"></span>
                ) : (
                  <SparklesIcon className="w-4 h-4 text-purple-500 flex-shrink-0" />
                )}
                <div>
                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                    {aiDetecting ? 'AI Analyzing...' : 'AI-Detect Relationships'}
                  </div>
                  {!aiDetecting && formatCost('ai_relationships') && (
                    <div className="text-xs text-purple-500">{formatCost('ai_relationships')}</div>
                  )}
                </div>
              </button>
              <button
                onClick={() => { setShowAddModal(true); setOpenDropdown(null); }}
                className="w-full px-4 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-3 transition-colors"
              >
                <PlusIcon className="w-4 h-4 text-gray-500 flex-shrink-0" />
                <div className="text-sm font-medium text-gray-900 dark:text-white">Add Manual Relationship</div>
              </button>
            </div>
          )}
        </div>

        {/* Create View Dropdown */}
        <div className="relative" ref={viewDropdownRef}>
          <button
            onClick={() => setOpenDropdown(openDropdown === 'view' ? null : 'view')}
            className="px-4 py-2.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg flex items-center gap-2 transition-colors shadow-sm"
          >
            <CircleStackIcon className="w-4 h-4" />
            <span className="font-medium">Create View</span>
            <ChevronDownIcon className={`w-4 h-4 transition-transform ${openDropdown === 'view' ? 'rotate-180' : ''}`} />
          </button>
          {openDropdown === 'view' && (
            <div className="absolute left-0 top-full mt-1 w-64 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-30 py-1">
              <button
                onClick={() => { setShowViewModal(true); setOpenDropdown(null); }}
                disabled={selectedRelationships.length === 0}
                className="w-full px-4 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-3 transition-colors"
              >
                <TableCellsIcon className="w-4 h-4 text-gray-500 flex-shrink-0" />
                <div>
                  <div className="text-sm font-medium text-gray-900 dark:text-white">Create from Selected</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {selectedRelationships.length === 0 ? 'Select relationships first' : `${selectedRelationships.length} selected`}
                  </div>
                </div>
              </button>
              <button
                onClick={() => { setShowCustomJoinModal(true); setOpenDropdown(null); }}
                className="w-full px-4 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-3 transition-colors"
              >
                <svg className="w-4 h-4 text-gray-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                </svg>
                <div className="text-sm font-medium text-gray-900 dark:text-white">&lt;/&gt; Custom Join View (SQL)</div>
              </button>
              <button
                onClick={() => { setShowUnionViewModal(true); setOpenDropdown(null); }}
                className="w-full px-4 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-3 transition-colors"
              >
                <svg className="w-4 h-4 text-gray-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
                <div className="text-sm font-medium text-gray-900 dark:text-white">Union Tables</div>
              </button>
            </div>
          )}
        </div>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Hero: AI Assistant */}
        <button
          onClick={() => setShowAIPanel(true)}
          className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2 transition-all shadow-md hover:shadow-lg font-medium"
        >
          <SparklesIcon className="w-5 h-5" />
          AI Assistant
          {formatCost('data_studio_build_step') && (
            <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">{formatCost('data_studio_build_step')}</span>
          )}
        </button>
      </div>

      {/* Relationships Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden mb-8">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Relationships ({relationships.length})
          </h2>
        </div>
        
        {relationships.length === 0 ? (
          <div className="p-12 text-center text-gray-500 dark:text-gray-400">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
            </svg>
            <p className="mt-2">No relationships defined yet.</p>
            <p className="text-sm">Click "Auto-Detect" to find relationships or add them manually.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="w-12 px-4 py-3">
                    <input
                      type="checkbox"
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedRelationships(relationships.map(r => r.id));
                        } else {
                          setSelectedRelationships([]);
                        }
                      }}
                      checked={selectedRelationships.length === relationships.length && relationships.length > 0}
                      className="rounded"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Source
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Target
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Confidence
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {relationships.map((rel) => (
                  <tr key={rel.id} className={rel.isActive ? '' : 'opacity-50'}>
                    <td className="px-4 py-4">
                      <input
                        type="checkbox"
                        checked={selectedRelationships.includes(rel.id)}
                        onChange={() => toggleRelationshipSelection(rel.id)}
                        className="rounded"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {rel.sourceTable}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        .{rel.sourceColumn}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <svg className="w-4 h-4 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                        </svg>
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {rel.targetTable}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            .{rel.targetColumn}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs font-medium bg-gray-100 dark:bg-gray-700 rounded">
                        {RELATIONSHIP_TYPES[rel.type] || rel.type}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className={`h-2 w-2 rounded-full ${getConfidenceColor(rel.confidence)} mr-2`}></div>
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          {Math.round(rel.confidence * 100)}%
                        </span>
                        {rel.isAutoDetected && (
                          <span className="ml-2 px-2 py-0.5 text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded">
                            Auto
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleToggleActive(rel.id, rel.isActive)}
                        className={`px-2 py-1 text-xs font-medium rounded ${
                          rel.isActive 
                            ? 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300' 
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                        }`}
                      >
                        {rel.isActive ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleEdit(rel)}
                          className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                          title="Edit relationship"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDelete(rel.id)}
                          className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                          title="Delete relationship"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Views Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Views ({views.length})
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Views combine related tables for easier querying. Preview data before deploying.
          </p>
        </div>
        
        {views.length === 0 ? (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400">
            <p>No views created yet.</p>
            <p className="text-sm">Select relationships and click "Create View" to combine tables.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
            {views.map((view, index) => (
              <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:border-purple-300 dark:hover:border-purple-700 transition-colors">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-medium text-gray-900 dark:text-white">{view.name}</h3>
                  <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 text-xs rounded ${
                    view.status === 'ready' 
                      ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                      : view.status === 'error'
                      ? 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300'
                      : view.status === 'deploying'
                      ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                      : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300'
                  }`}>
                    {view.status}
                  </span>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 mb-1">
                  <span className={`px-1.5 py-0.5 text-xs rounded ${
                    view.combineMode === 'union'
                      ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900 dark:text-indigo-300'
                      : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                  }`}>
                    {view.combineMode === 'union' ? 'UNION' : 'JOIN'}
                  </span>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {view.combineMode === 'union' ? 'Primary' : 'Base'}: {view.baseTable}
                </p>
                {view.combineMode === 'union' ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Stacked with: {view.unionTables?.map(t => t.table).join(', ') || 'N/A'}
                  </p>
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Joins: {view.includedTables?.map(t => t.table).join(', ') || 'N/A'}
                  </p>
                )}
                {view.selectedColumns?.length > 0 && (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Columns: {view.selectedColumns.length} selected
                  </p>
                )}
                {view.calculatedColumns?.length > 0 && (
                  <div className="mt-1">
                    <p className="text-xs font-medium text-blue-600 dark:text-blue-400">
                      + {view.calculatedColumns.length} calculated: {view.calculatedColumns.map(c => c.name).join(', ')}
                    </p>
                  </div>
                )}
                {view.bigQueryViewName && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                    BigQuery: {view.bigQueryViewName}
                  </p>
                )}
                
                {/* Action buttons */}
                <div className="flex items-center justify-end gap-2 mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                  <button
                    onClick={() => handlePreview(view.name)}
                    disabled={previewing}
                    className="px-3 py-1.5 text-sm text-purple-600 hover:text-purple-800 dark:text-purple-400 dark:hover:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded flex items-center gap-1"
                    title="Preview data"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    Preview
                  </button>
                  {view.status !== 'ready' && (
                    <button
                      onClick={() => handleDeploy(view.name)}
                      disabled={generating || view.status === 'deploying'}
                      className="px-3 py-1.5 text-sm text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300 hover:bg-green-50 dark:hover:bg-green-900/20 rounded flex items-center gap-1"
                      title="Deploy to BigQuery"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      Deploy
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setEditingView(view);
                      setShowEditViewModal(true);
                    }}
                    className="px-3 py-1.5 text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded flex items-center gap-1"
                    title="Edit view"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    Edit
                  </button>
                  <button
                    onClick={async () => {
                      if (!window.confirm(`Are you sure you want to delete the view "${view.name}"?`)) return;
                      try {
                        await api.delete(`/relationships/views/${encodeURIComponent(view.name)}`, { params: { companyId } });
                        setSuccess('View deleted');
                        await fetchData();
                      } catch (err) {
                        setError('Failed to delete view');
                      }
                    }}
                    className="px-3 py-1.5 text-sm text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded flex items-center gap-1"
                    title="Delete view"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      </div>

      {/* Preview Modal */}
      {previewData && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Preview: {previewData.viewName}
                </h3>
                <p className="text-sm text-gray-500">
                  {previewData.rowCount} rows × {previewData.columns?.length} columns
                </p>
              </div>
              <button
                onClick={() => setPreviewData(null)}
                className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {previewData.success ? (
              <div className="flex-1 overflow-auto p-4">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
                    <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0">
                      <tr>
                        {previewData.columns?.map((col, i) => (
                          <th key={i} className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap">
                            {col}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {previewData.data?.slice(0, 100).map((row, i) => (
                        <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                          {previewData.columns?.map((col, j) => (
                            <td key={j} className="px-3 py-2 whitespace-nowrap text-gray-900 dark:text-gray-100">
                              {row[col] !== null && row[col] !== undefined ? String(row[col]).substring(0, 100) : <span className="text-gray-400 italic">null</span>}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {previewData.sql && (
                  <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Generated SQL:</h4>
                    <pre className="text-xs text-gray-600 dark:text-gray-400 overflow-x-auto whitespace-pre-wrap">{previewData.sql}</pre>
                  </div>
                )}
              </div>
            ) : (
              <div className="p-8 text-center text-red-500">
                <p>Failed to preview: {previewData.error}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Pending Review Modal — previously deleted relationships that AI re-detected */}
      {showPendingReviewModal && pendingReviewItems.length > 0 && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-3xl w-full mx-4 max-h-[80vh] flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                  <svg className="w-5 h-5 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Previously Removed Relationships Re-detected
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    The AI found {pendingReviewItems.length} relationship{pendingReviewItems.length !== 1 ? 's' : ''} that you previously removed. Would you like to add any of them back?
                  </p>
                </div>
              </div>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
              {pendingReviewItems.map((item, index) => {
                const selection = pendingSelections[index];
                return (
                  <div
                    key={index}
                    className={`p-4 rounded-lg border-2 transition-colors ${
                      selection === 'accept'
                        ? 'border-green-300 bg-green-50 dark:border-green-600 dark:bg-green-900/20'
                        : selection === 'reject'
                        ? 'border-red-300 bg-red-50 dark:border-red-600 dark:bg-red-900/20'
                        : 'border-gray-200 bg-gray-50 dark:border-gray-600 dark:bg-gray-700/50'
                    }`}
                  >
                    <div className="space-y-3">
                      {/* Relationship details — stacked layout for readability */}
                      <div className="flex items-center gap-3 flex-wrap">
                        {/* Source */}
                        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 text-sm">
                          <div className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-0.5">Source</div>
                          <span className="font-semibold text-gray-900 dark:text-white">{item.sourceTable}</span>
                          <span className="text-gray-400 mx-1">.</span>
                          <span className="text-blue-600 dark:text-blue-400 font-medium">{item.sourceColumn}</span>
                        </div>
                        
                        {/* Arrow */}
                        <svg className="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                        </svg>
                        
                        {/* Target */}
                        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 text-sm">
                          <div className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-0.5">Target</div>
                          <span className="font-semibold text-gray-900 dark:text-white">{item.targetTable}</span>
                          <span className="text-gray-400 mx-1">.</span>
                          <span className="text-blue-600 dark:text-blue-400 font-medium">{item.targetColumn}</span>
                        </div>
                      </div>
                      
                      {/* Metadata row */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-xs px-2 py-0.5 rounded-full bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300">
                            {(item.type || 'one_to_many').replace(/_/g, ' ')}
                          </span>
                          {item.confidence && (
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {Math.round(item.confidence * 100)}% confidence
                            </span>
                          )}
                          {item.description && (
                            <span className="text-xs text-gray-500 dark:text-gray-400 italic">
                              — {item.description}
                            </span>
                          )}
                        </div>
                        
                        {/* Action buttons */}
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <button
                            onClick={() => setPendingSelections(prev => ({ ...prev, [index]: prev[index] === 'accept' ? undefined : 'accept' }))}
                            className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                              selection === 'accept'
                                ? 'bg-green-600 text-white'
                                : 'bg-white dark:bg-gray-700 text-green-600 dark:text-green-400 border border-green-300 dark:border-green-600 hover:bg-green-50 dark:hover:bg-green-900/20'
                            }`}
                          >
                            Add Back
                          </button>
                          <button
                            onClick={() => setPendingSelections(prev => ({ ...prev, [index]: prev[index] === 'reject' ? undefined : 'reject' }))}
                            className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                              selection === 'reject'
                                ? 'bg-red-600 text-white'
                                : 'bg-white dark:bg-gray-700 text-red-600 dark:text-red-400 border border-red-300 dark:border-red-600 hover:bg-red-50 dark:hover:bg-red-900/20'
                            }`}
                          >
                            Keep Removed
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <div className="text-sm text-gray-500 dark:text-gray-400">
                {Object.values(pendingSelections).filter(v => v === 'accept').length} to add back,{' '}
                {pendingReviewItems.length - Object.values(pendingSelections).filter(v => v === 'accept').length} to keep removed
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => {
                    // Dismiss all — treat unselected as reject
                    setShowPendingReviewModal(false);
                    setPendingReviewItems([]);
                    setPendingSelections({});
                  }}
                  className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
                  disabled={resolvingPending}
                >
                  Skip for Now
                </button>
                <button
                  onClick={handleResolvePending}
                  disabled={resolvingPending}
                  className="px-4 py-2 text-sm font-medium bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg transition-colors flex items-center gap-2"
                >
                  {resolvingPending ? (
                    <>
                      <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span>
                      Saving...
                    </>
                  ) : (
                    'Confirm Choices'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Relationship Modal */}
      {showAddModal && (
        <RelationshipModal
          mode="add"
          dataSources={dataSources}
          onClose={() => setShowAddModal(false)}
          onSave={async (data) => {
            try {
              await api.post('/relationships', { ...data, companyId });
              setSuccess('Relationship added');
              setShowAddModal(false);
              await fetchData();
            } catch (err) {
              setError('Failed to add relationship');
            }
          }}
        />
      )}

      {/* Edit Relationship Modal */}
      {showEditModal && editingRelationship && (
        <RelationshipModal
          mode="edit"
          dataSources={dataSources}
          relationship={editingRelationship}
          onClose={() => {
            setShowEditModal(false);
            setEditingRelationship(null);
          }}
          onSave={(data) => handleUpdate(editingRelationship.id, data)}
        />
      )}

      {/* Create View Modal */}
      {showViewModal && (
        <CreateViewModal
          relationships={relationships.filter(r => selectedRelationships.includes(r.id))}
          dataSources={dataSources}
          onClose={() => setShowViewModal(false)}
          onCreate={async (data) => {
            try {
              await api.post('/relationships/views', {
                ...data,
                companyId,
                relationshipIds: selectedRelationships
              });
              setSuccess('View created');
              setShowViewModal(false);
              setSelectedRelationships([]);
              await fetchData();
            } catch (err) {
              setError('Failed to create view');
            }
          }}
        />
      )}

      {/* Custom Join View Modal */}
      {showCustomJoinModal && (
        <CreateViewModal
          relationships={[]}
          dataSources={dataSources}
          customJoinMode={true}
          onClose={() => setShowCustomJoinModal(false)}
          onCreate={async (data) => {
            try {
              await api.post('/relationships/views', {
                ...data,
                companyId
              });
              setSuccess('Custom join view created');
              setShowCustomJoinModal(false);
              await fetchData();
            } catch (err) {
              setError('Failed to create custom join view');
            }
          }}
        />
      )}

      {/* Create Union View Modal */}
      {showUnionViewModal && (
        <CreateUnionViewModal
          dataSources={dataSources}
          onClose={() => setShowUnionViewModal(false)}
          onCreate={async (data) => {
            try {
              await api.post('/relationships/views', {
                ...data,
                companyId,
                combineMode: 'union'
              });
              setSuccess('Union view created');
              setShowUnionViewModal(false);
              await fetchData();
            } catch (err) {
              setError('Failed to create union view');
            }
          }}
        />
      )}

      {/* Edit View Modal */}
      {showEditViewModal && editingView && (
        <EditViewModal
          view={editingView}
          relationships={relationships}
          dataSources={dataSources}
          onClose={() => {
            setShowEditViewModal(false);
            setEditingView(null);
          }}
          onSave={async (data) => {
            try {
              const response = await api.put(`/relationships/views/${encodeURIComponent(editingView.name)}`, {
                ...data,
                companyId
              });
              const result = response.data;
              if (result.deployed) {
                setSuccess('View updated and deployed to BigQuery!');
              } else if (result.error) {
                setSuccess(`View saved. Note: deployment failed - ${result.error}`);
              } else {
                setSuccess('View updated');
              }
              setShowEditViewModal(false);
              setEditingView(null);
              await fetchData();
            } catch (err) {
              setError(err.response?.data?.error || 'Failed to update view');
              throw err; // Re-throw to let modal know it failed
            }
          }}
        />
      )}

      {/* AI Assistant Slide-out Panel */}
      {showAIPanel && (
        <div className="fixed inset-0 z-50 overflow-hidden">
          <div className="absolute inset-0 bg-black/30" onClick={() => setShowAIPanel(false)} />
          <div className="absolute inset-y-0 right-0 max-w-md w-full flex">
            <div className="relative w-full bg-white dark:bg-gray-800 shadow-xl flex flex-col animate-slide-in-right">
              {/* AI Panel Header */}
              <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between bg-blue-600">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                    <SparklesIcon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">AI Assistant</h3>
                    <p className="text-xs text-white/70">
                      {formatCost('data_studio_build_step')} per action
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {/* History dropdown */}
                  <div className="relative history-dropdown">
                    <button
                      onClick={() => setShowHistory(!showHistory)}
                      className="p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors flex items-center gap-1"
                      title="Chat history"
                    >
                      <ChatBubbleLeftRightIcon className="w-5 h-5" />
                      <ChevronDownIcon className={`w-4 h-4 transition-transform ${showHistory ? 'rotate-180' : ''}`} />
                    </button>
                    
                    {showHistory && (
                      <div className="absolute right-0 top-full mt-1 w-72 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 z-50 max-h-80 overflow-y-auto">
                        <div className="p-2 border-b border-gray-200 dark:border-gray-700">
                          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                            Recent Conversations
                          </p>
                        </div>
                        {conversationsData?.data?.length > 0 ? (
                          <div className="p-1">
                            {conversationsData.data.map((conv) => (
                              <div
                                key={conv._id}
                                className={`flex items-center justify-between p-2 rounded-lg cursor-pointer group ${
                                  conversationId === conv._id
                                    ? 'bg-purple-50 dark:bg-purple-900/30'
                                    : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                                }`}
                              >
                                <div
                                  className="flex-1 min-w-0"
                                  onClick={() => loadConversation(conv._id)}
                                >
                                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                    {conv.title || 'Untitled'}
                                  </p>
                                  <p className="text-xs text-gray-500 dark:text-gray-400">
                                    {new Date(conv.lastMessageAt || conv.createdAt).toLocaleDateString()}
                                  </p>
                                </div>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    deleteConversation(conv._id);
                                  }}
                                  className="p-1 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                  <TrashIcon className="w-4 h-4" />
                                </button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="p-4 text-sm text-gray-500 dark:text-gray-400 text-center">
                            No previous conversations
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {/* New Chat button */}
                  <button
                    onClick={startNewChat}
                    className="p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                    title="New chat"
                  >
                    <PlusIcon className="w-5 h-5" />
                  </button>
                  
                  {/* Close button */}
                  <button
                    onClick={() => setShowAIPanel(false)}
                    className="p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                    title="Close"
                  >
                    <XMarkIcon className="w-5 h-5" />
                  </button>
                </div>
              </div>
              
              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages.length === 0 ? (
                  <div className="text-center py-8">
                    <SparklesIcon className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                    <p className="text-gray-500 dark:text-gray-400 mb-6">
                      Hi! I'm your Data Studio assistant. I can help you:
                    </p>
                    <ul className="text-left text-sm text-gray-600 dark:text-gray-400 space-y-2 max-w-xs mx-auto mb-6">
                      <li className="flex items-center gap-2">
                        <CheckCircleIcon className="w-4 h-4 text-green-500" />
                        Find connections between your data
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircleIcon className="w-4 h-4 text-green-500" />
                        Create Views that combine tables
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircleIcon className="w-4 h-4 text-green-500" />
                        Preview data before deploying
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircleIcon className="w-4 h-4 text-green-500" />
                        Set up everything automatically
                      </li>
                    </ul>
                    
                    {/* Suggestion Chips */}
                    <div className="flex flex-wrap gap-2 justify-center">
                      {SUGGESTION_CHIPS.map((chip, idx) => (
                        <button
                          key={idx}
                          onClick={() => handleChipClick(chip.label)}
                          className="px-3 py-1.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-full text-sm transition-colors flex items-center gap-1.5"
                        >
                          <chip.icon className="w-4 h-4" />
                          {chip.label}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <>
                    {messages.map((msg, idx) => (
                      <MessageBubble key={idx} message={msg} />
                    ))}
                    {isTyping && (
                      <div className="flex items-center gap-2 text-gray-500">
                        <div className="flex gap-1">
                          <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                          <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                          <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                        </div>
                        <span className="text-sm">Thinking...</span>
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </>
                )}
              </div>
              
              {/* Input */}
              <div className="p-4 border-t border-gray-200 dark:border-gray-700">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Ask about your data..."
                    className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    disabled={chatMutation.isPending}
                  />
                  <button
                    onClick={() => handleSendMessage()}
                    disabled={!message.trim() || chatMutation.isPending}
                    className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 text-white rounded-lg transition-colors flex items-center gap-2"
                    title={`Send message ${formatCost('data_studio_build_step')}`}
                  >
                    <PaperAirplaneIcon className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Relationship Modal Component (Add/Edit)
const RelationshipModal = ({ mode = 'add', dataSources, relationship = null, onClose, onSave }) => {
  const isEdit = mode === 'edit';
  
  const [formData, setFormData] = useState({
    sourceTable: relationship?.sourceTable || '',
    sourceColumn: relationship?.sourceColumn || '',
    targetTable: relationship?.targetTable || '',
    targetColumn: relationship?.targetColumn || '',
    type: relationship?.type || 'one_to_many',
    description: relationship?.description || ''
  });

  const tables = dataSources.map(ds => ({
    name: ds.bigQueryTable,
    columns: ds.schemaInfo?.tables?.[0]?.columns || []
  }));

  const sourceColumns = tables.find(t => t.name === formData.sourceTable)?.columns || [];
  const targetColumns = tables.find(t => t.name === formData.targetTable)?.columns || [];

  // Check if the current column value exists in the available columns
  // This handles cases where the table exists but maybe columns aren't loaded yet
  const isValidSourceColumn = !formData.sourceColumn || sourceColumns.some(c => c.name === formData.sourceColumn);
  const isValidTargetColumn = !formData.targetColumn || targetColumns.some(c => c.name === formData.targetColumn);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full mx-4">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {isEdit ? 'Edit Relationship' : 'Add Relationship'}
          </h3>
        </div>
        
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Source Table</label>
              <select
                value={formData.sourceTable}
                onChange={(e) => setFormData(prev => ({ ...prev, sourceTable: e.target.value, sourceColumn: '' }))}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 dark:bg-gray-700"
              >
                <option value="">Select table</option>
                {tables.map(t => (
                  <option key={t.name} value={t.name}>{t.name}</option>
                ))}
                {/* Show current value if not in list (for edit mode) */}
                {isEdit && formData.sourceTable && !tables.find(t => t.name === formData.sourceTable) && (
                  <option value={formData.sourceTable}>{formData.sourceTable}</option>
                )}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Source Column</label>
              <select
                value={formData.sourceColumn}
                onChange={(e) => setFormData(prev => ({ ...prev, sourceColumn: e.target.value }))}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 dark:bg-gray-700"
                disabled={!formData.sourceTable}
              >
                <option value="">Select column</option>
                {sourceColumns.map(c => (
                  <option key={c.name} value={c.name}>{c.name} ({c.type})</option>
                ))}
                {/* Show current value if not in list (for edit mode) */}
                {isEdit && formData.sourceColumn && !sourceColumns.find(c => c.name === formData.sourceColumn) && (
                  <option value={formData.sourceColumn}>{formData.sourceColumn}</option>
                )}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Target Table</label>
              <select
                value={formData.targetTable}
                onChange={(e) => setFormData(prev => ({ ...prev, targetTable: e.target.value, targetColumn: '' }))}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 dark:bg-gray-700"
              >
                <option value="">Select table</option>
                {tables.map(t => (
                  <option key={t.name} value={t.name}>{t.name}</option>
                ))}
                {/* Show current value if not in list (for edit mode) */}
                {isEdit && formData.targetTable && !tables.find(t => t.name === formData.targetTable) && (
                  <option value={formData.targetTable}>{formData.targetTable}</option>
                )}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Target Column</label>
              <select
                value={formData.targetColumn}
                onChange={(e) => setFormData(prev => ({ ...prev, targetColumn: e.target.value }))}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 dark:bg-gray-700"
                disabled={!formData.targetTable}
              >
                <option value="">Select column</option>
                {targetColumns.map(c => (
                  <option key={c.name} value={c.name}>{c.name} ({c.type})</option>
                ))}
                {/* Show current value if not in list (for edit mode) */}
                {isEdit && formData.targetColumn && !targetColumns.find(c => c.name === formData.targetColumn) && (
                  <option value={formData.targetColumn}>{formData.targetColumn}</option>
                )}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Relationship Type</label>
            <select
              value={formData.type}
              onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 dark:bg-gray-700"
            >
              {Object.entries(RELATIONSHIP_TYPES).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description (optional)</label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="e.g., Links orders to customers"
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 dark:bg-gray-700"
            />
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
          >
            Cancel
          </button>
          <button
            onClick={() => onSave(formData)}
            disabled={!formData.sourceTable || !formData.sourceColumn || !formData.targetTable || !formData.targetColumn}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg"
          >
            {isEdit ? 'Save Changes' : 'Add Relationship'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Data types for calculated columns
const DATA_TYPES = [
  { value: 'FLOAT', label: 'Decimal (FLOAT)' },
  { value: 'INTEGER', label: 'Whole Number (INTEGER)' },
  { value: 'STRING', label: 'Text (STRING)' },
  { value: 'BOOLEAN', label: 'True/False (BOOLEAN)' },
  { value: 'TIMESTAMP', label: 'Date & Time (TIMESTAMP)' },
  { value: 'DATE', label: 'Date (DATE)' }
];

// Example expressions for calculated columns
const EXPRESSION_EXAMPLES = [
  { label: 'Sum', example: 'column1 + column2' },
  { label: 'Difference', example: 'column1 - column2' },
  { label: 'Multiply', example: 'column1 * column2' },
  { label: 'Divide', example: 'column1 / NULLIF(column2, 0)' },
  { label: 'Percentage', example: 'ROUND(column1 / column2 * 100, 2)' },
  { label: 'Concat', example: "CONCAT(column1, ' - ', column2)" },
  { label: 'Date Diff Days', example: 'DATE_DIFF(end_date, start_date, DAY)' },
  { label: 'Case When', example: "CASE WHEN column1 > 100 THEN 'High' ELSE 'Low' END" }
];

// Create View Modal Component
const CreateViewModal = ({ relationships, dataSources, onClose, onCreate, customJoinMode = false }) => {
  const [name, setName] = useState('');
  const [baseTable, setBaseTable] = useState('');
  const [calculatedColumns, setCalculatedColumns] = useState([]);
  const [selectedColumns, setSelectedColumns] = useState({}); // { tableName: ['col1', 'col2'] }
  const [selectAllTables, setSelectAllTables] = useState({}); // { tableName: true/false }
  const [columnSearchTerm, setColumnSearchTerm] = useState('');
  const [showAddCalcColumn, setShowAddCalcColumn] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createProgress, setCreateProgress] = useState('');
  const [newCalcColumn, setNewCalcColumn] = useState({
    name: '',
    expression: '',
    description: '',
    dataType: 'FLOAT'
  });

  // Custom joins state
  const [customJoins, setCustomJoins] = useState([]);
  const [showAddCustomJoin, setShowAddCustomJoin] = useState(false);
  const [newCustomJoin, setNewCustomJoin] = useState({
    table: '',
    joinType: 'LEFT',
    condition: ''
  });

  // All BigQuery tables from data sources (for base table selection in custom join mode)
  const allBQTables = dataSources
    .filter(ds => ds.status === 'ready' && ds.bigQueryTable)
    .map(ds => ({ table: ds.bigQueryTable, name: ds.name }));

  // Get unique tables from relationships
  const relationshipTables = [...new Set([
    ...relationships.map(r => r.sourceTable),
    ...relationships.map(r => r.targetTable)
  ])];

  // In custom join mode, base table comes from all data sources; otherwise from relationships
  const baseTables = customJoinMode ? allBQTables.map(ds => ds.table) : relationshipTables;

  // Effective joined tables = relationship tables + custom join tables
  const customJoinTables = customJoins.map(cj => cj.table);
  const tables = [...new Set([...relationshipTables, ...customJoinTables])];

  // Get columns for a table from dataSources
  const getColumnsForTable = (tableName) => {
    const ds = dataSources.find(d => d.bigQueryTable === tableName);
    return ds?.schemaInfo?.tables?.[0]?.columns || [];
  };

  // Initialize selectAllTables when baseTable or custom joins change
  React.useEffect(() => {
    if (baseTable) {
      const allTables = [...new Set([...relationshipTables, ...customJoinTables])];
      const initialSelectAll = {};
      const initialSelected = {};
      allTables.forEach(t => {
        if (t === baseTable) {
          initialSelectAll[t] = true;
          initialSelected[t] = getColumnsForTable(t).map(c => c.name);
        } else {
          initialSelectAll[t] = selectAllTables[t] ?? false;
          initialSelected[t] = selectedColumns[t] ?? [];
        }
      });
      setSelectAllTables(initialSelectAll);
      setSelectedColumns(initialSelected);
    }
  }, [baseTable, customJoins.length]);

  // Toggle all columns for a table
  const handleToggleAllColumns = (tableName, checked) => {
    setSelectAllTables(prev => ({ ...prev, [tableName]: checked }));
    if (checked) {
      const allCols = getColumnsForTable(tableName).map(c => c.name);
      setSelectedColumns(prev => ({ ...prev, [tableName]: allCols }));
    } else {
      setSelectedColumns(prev => ({ ...prev, [tableName]: [] }));
    }
  };

  // Toggle individual column
  const handleToggleColumn = (tableName, columnName, checked) => {
    setSelectedColumns(prev => {
      const current = prev[tableName] || [];
      if (checked) {
        return { ...prev, [tableName]: [...current, columnName] };
      } else {
        return { ...prev, [tableName]: current.filter(c => c !== columnName) };
      }
    });
    // Update selectAll state
    const allCols = getColumnsForTable(tableName).map(c => c.name);
    const newSelected = selectedColumns[tableName] || [];
    if (checked) {
      setSelectAllTables(prev => ({ ...prev, [tableName]: newSelected.length + 1 === allCols.length }));
    } else {
      setSelectAllTables(prev => ({ ...prev, [tableName]: false }));
    }
  };

  // Build selectedColumns array for API
  // Uses wildcard for a fully-selected base table so backend can COALESCE same-named columns
  const buildSelectedColumnsArray = () => {
    const result = [];
    for (const [tableName, columns] of Object.entries(selectedColumns)) {
      if (columns.length === 0) continue;
      const source = tableName === baseTable ? 'base' : tableName;
      const allCols = getColumnsForTable(tableName).map(c => c.name);
      if (columns.length === allCols.length) {
        // All columns selected — use wildcard
        result.push({ source, column: '*', alias: null });
      } else {
        for (const col of columns) {
          result.push({ source, column: col, alias: null });
        }
      }
    }
    return result;
  };

  // Count total selected columns (wildcards count as all columns of that table)
  const getTotalSelectedColumns = () => {
    return Object.values(selectedColumns).reduce((sum, cols) => sum + cols.length, 0);
  };

  const handleAddCalculatedColumn = () => {
    if (!newCalcColumn.name || !newCalcColumn.expression) return;
    
    setCalculatedColumns(prev => [...prev, { ...newCalcColumn }]);
    setNewCalcColumn({ name: '', expression: '', description: '', dataType: 'FLOAT' });
    setShowAddCalcColumn(false);
  };

  const handleRemoveCalculatedColumn = (index) => {
    setCalculatedColumns(prev => prev.filter((_, i) => i !== index));
  };

  // Handle create with loading state
  const handleCreate = async () => {
    setCreating(true);
    setCreateProgress('Creating wide view...');
    
    try {
      setCreateProgress('Generating dbt model and materializing in BigQuery...');
      const payload = { 
        name, 
        baseTable, 
        calculatedColumns,
        selectedColumns: buildSelectedColumnsArray()
      };
      if (customJoins.length > 0) {
        payload.customJoins = customJoins;
      }
      await onCreate(payload);
      setCreateProgress('Complete!');
    } catch (error) {
      setCreateProgress('');
      setCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-800">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {customJoinMode ? 'Create Custom Join View' : 'Create View'}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {customJoinMode
              ? 'Join tables using custom SQL conditions — no pre-existing relationship required.'
              : 'Combine tables and add calculated columns for derived metrics.'}
          </p>
        </div>
        
        <div className="p-6 space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">View Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., customer_orders_complete"
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 dark:bg-gray-700"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Base Table</label>
            <select
              value={baseTable}
              onChange={(e) => setBaseTable(e.target.value)}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 dark:bg-gray-700"
            >
              <option value="">Select base table</option>
              {baseTables.map(t => {
                const ds = allBQTables.find(d => d.table === t);
                return <option key={t} value={t}>{ds ? `${ds.name} (${t})` : t}</option>;
              })}
            </select>
            </div>
          </div>

          {/* Selected Relationships (hidden in custom-join-only mode if none) */}
          {relationships.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Selected Relationships</label>
            <div className="space-y-2 max-h-32 overflow-y-auto">
              {relationships.map((rel, index) => (
                <div key={index} className="text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 px-3 py-2 rounded flex items-center gap-2">
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                  </svg>
                  {rel.sourceTable}.{rel.sourceColumn} → {rel.targetTable}.{rel.targetColumn}
                </div>
              ))}
            </div>
          </div>
          )}

          {/* Custom Joins Section */}
          {(customJoinMode || customJoins.length > 0) && (
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Custom Joins</label>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Join tables using custom SQL conditions — no relationship required
                </p>
              </div>
              <button
                onClick={() => setShowAddCustomJoin(true)}
                disabled={!baseTable}
                className="px-3 py-1.5 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white text-sm rounded-lg flex items-center gap-1"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Custom Join
              </button>
            </div>

            {customJoins.length > 0 && (
              <div className="space-y-2 mb-3">
                {customJoins.map((cj, index) => {
                  const ds = allBQTables.find(d => d.table === cj.table);
                  return (
                    <div key={index} className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="px-2 py-0.5 text-xs bg-green-100 dark:bg-green-800 text-green-700 dark:text-green-300 rounded">
                              {cj.joinType} JOIN
                            </span>
                            <span className="font-medium text-green-800 dark:text-green-300 text-sm truncate">
                              {ds ? ds.name : cj.table}
                            </span>
                          </div>
                          <code className="text-xs text-gray-600 dark:text-gray-400 block mt-1 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded break-all">
                            ON {cj.condition}
                          </code>
                        </div>
                        <button
                          onClick={() => setCustomJoins(prev => prev.filter((_, i) => i !== index))}
                          className="text-red-500 hover:text-red-700 p-1 ml-2 flex-shrink-0"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {customJoins.length === 0 && !showAddCustomJoin && (
              <div className="text-center py-4 text-gray-500 dark:text-gray-400 text-sm border border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
                No custom joins added yet. Use custom joins when tables have no direct relationship but can be linked via SQL expressions.
              </div>
            )}

            {showAddCustomJoin && (
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 space-y-3 border border-gray-200 dark:border-gray-600">
                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-2">
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Table to Join</label>
                    <select
                      value={newCustomJoin.table}
                      onChange={(e) => setNewCustomJoin(prev => ({ ...prev, table: e.target.value }))}
                      className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm dark:bg-gray-700"
                    >
                      <option value="">Select a table...</option>
                      {allBQTables
                        .filter(ds => ds.table !== baseTable && !customJoins.some(cj => cj.table === ds.table))
                        .map(ds => (
                          <option key={ds.table} value={ds.table}>{ds.name} ({ds.table})</option>
                        ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Join Type</label>
                    <select
                      value={newCustomJoin.joinType}
                      onChange={(e) => setNewCustomJoin(prev => ({ ...prev, joinType: e.target.value }))}
                      className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm dark:bg-gray-700"
                    >
                      <option value="LEFT">LEFT JOIN</option>
                      <option value="INNER">INNER JOIN</option>
                      <option value="RIGHT">RIGHT JOIN</option>
                      <option value="FULL">FULL JOIN</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                    SQL ON Condition
                  </label>
                  <textarea
                    value={newCustomJoin.condition}
                    onChange={(e) => setNewCustomJoin(prev => ({ ...prev, condition: e.target.value }))}
                    placeholder="e.g., REGEXP_EXTRACT(base.pagePath, '/public/dashboard/([^?#]+)') = join_table.slug"
                    rows={2}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm font-mono dark:bg-gray-700"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Use <code className="bg-gray-200 dark:bg-gray-600 px-1 rounded">base.</code> for base table columns and <code className="bg-gray-200 dark:bg-gray-600 px-1 rounded">join_table.</code> for the joined table columns.
                  </p>
                </div>

                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => {
                      setShowAddCustomJoin(false);
                      setNewCustomJoin({ table: '', joinType: 'LEFT', condition: '' });
                    }}
                    className="px-3 py-1.5 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600 text-sm rounded"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      if (!newCustomJoin.table || !newCustomJoin.condition) return;
                      setCustomJoins(prev => [...prev, { ...newCustomJoin }]);
                      setNewCustomJoin({ table: '', joinType: 'LEFT', condition: '' });
                      setShowAddCustomJoin(false);
                    }}
                    disabled={!newCustomJoin.table || !newCustomJoin.condition}
                    className="px-3 py-1.5 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white text-sm rounded"
                  >
                    Add Join
                  </button>
                </div>
              </div>
            )}
          </div>
          )}

          {/* Column Selection Section */}
          {baseTable && (
            <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Select Columns</label>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Choose which columns to include from each table ({getTotalSelectedColumns()} selected)
                  </p>
          </div>
        </div>

              {/* Column Search */}
              <div className="relative mb-3">
                <svg className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  value={columnSearchTerm}
                  onChange={(e) => setColumnSearchTerm(e.target.value)}
                  placeholder="Search columns..."
                  className="w-full pl-9 pr-8 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {columnSearchTerm && (
                  <button
                    onClick={() => setColumnSearchTerm('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    ×
                  </button>
                )}
              </div>

              <div className="space-y-4 max-h-64 overflow-y-auto">
                {/* Base Table Columns */}
                {getColumnsForTable(baseTable).filter(col => col.name.toLowerCase().includes(columnSearchTerm.toLowerCase())).length > 0 && (
                <div className="border border-purple-200 dark:border-purple-800 rounded-lg overflow-hidden">
                  <div className="bg-purple-50 dark:bg-purple-900/30 px-3 py-2 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 text-xs bg-purple-200 dark:bg-purple-800 text-purple-700 dark:text-purple-300 rounded">BASE</span>
                      <span className="font-medium text-gray-900 dark:text-white text-sm">{baseTable}</span>
                      <span className="text-xs text-gray-500">
                        ({(selectedColumns[baseTable] || []).length}/{getColumnsForTable(baseTable).length} columns)
                      </span>
                    </div>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={selectAllTables[baseTable] || false}
                        onChange={(e) => handleToggleAllColumns(baseTable, e.target.checked)}
                        className="rounded text-purple-600"
                      />
                      <span className="text-gray-600 dark:text-gray-400">All</span>
                    </label>
                  </div>
                  <div className="p-3 grid grid-cols-2 md:grid-cols-3 gap-2 max-h-40 overflow-y-auto bg-white dark:bg-gray-800">
                    {getColumnsForTable(baseTable)
                      .filter(col => col.name.toLowerCase().includes(columnSearchTerm.toLowerCase()))
                      .map((col) => (
                      <label key={col.name} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 px-2 py-1 rounded">
                        <input
                          type="checkbox"
                          checked={(selectedColumns[baseTable] || []).includes(col.name)}
                          onChange={(e) => handleToggleColumn(baseTable, col.name, e.target.checked)}
                          className="rounded text-purple-600"
                        />
                        <span className="text-gray-700 dark:text-gray-300 truncate" title={`${col.name} (${col.type})`}>
                          {col.name}
                        </span>
                        <span className="text-xs text-gray-400 hidden md:inline">{col.type}</span>
                      </label>
                    ))}
                  </div>
                </div>
                )}

                {/* Joined Tables Columns */}
                {tables.filter(t => t !== baseTable).map((tableName) => {
                  const filteredCols = getColumnsForTable(tableName).filter(col => col.name.toLowerCase().includes(columnSearchTerm.toLowerCase()));
                  if (filteredCols.length === 0) return null;
                  const selectedCount = (selectedColumns[tableName] || []).length;
                  return (
                  <div key={tableName} className={`border rounded-lg overflow-hidden ${selectedCount === 0 ? 'border-amber-300 dark:border-amber-700' : 'border-gray-200 dark:border-gray-700'}`}>
                    <div className="bg-gray-50 dark:bg-gray-700 px-3 py-2 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-0.5 text-xs bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded">JOIN</span>
                        <span className="font-medium text-gray-900 dark:text-white text-sm">{tableName}</span>
                        <span className={`text-xs ${selectedCount === 0 ? 'text-amber-500 dark:text-amber-400 font-medium' : 'text-gray-500'}`}>
                          {selectedCount === 0 ? 'none selected — pick columns below' : `${selectedCount}/${getColumnsForTable(tableName).length} columns`}
                        </span>
                      </div>
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={selectAllTables[tableName] || false}
                          onChange={(e) => handleToggleAllColumns(tableName, e.target.checked)}
                          className="rounded text-blue-600"
                        />
                        <span className="text-gray-600 dark:text-gray-400">All</span>
                      </label>
                    </div>
                    <div className="p-3 grid grid-cols-2 md:grid-cols-3 gap-2 max-h-40 overflow-y-auto bg-white dark:bg-gray-800">
                      {filteredCols.map((col) => (
                        <label key={col.name} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 px-2 py-1 rounded">
                          <input
                            type="checkbox"
                            checked={(selectedColumns[tableName] || []).includes(col.name)}
                            onChange={(e) => handleToggleColumn(tableName, col.name, e.target.checked)}
                            className="rounded text-blue-600"
                          />
                          <span className="text-gray-700 dark:text-gray-300 truncate" title={`${col.name} (${col.type})`}>
                            {col.name}
                          </span>
                          <span className="text-xs text-gray-400 hidden md:inline">{col.type}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  );
                })}

                {/* No results message */}
                {columnSearchTerm && tables.every(t => getColumnsForTable(t).filter(col => col.name.toLowerCase().includes(columnSearchTerm.toLowerCase())).length === 0) && (
                  <div className="text-center py-4 text-gray-500 text-sm">
                    No columns match "{columnSearchTerm}"
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Calculated Columns Section */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Calculated Columns</label>
                <p className="text-xs text-gray-500 dark:text-gray-400">Add derived columns using SQL expressions</p>
              </div>
              <button
                onClick={() => setShowAddCalcColumn(true)}
                className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg flex items-center gap-1"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Column
              </button>
            </div>

            {/* List of calculated columns */}
            {calculatedColumns.length > 0 && (
              <div className="space-y-2 mb-4">
                {calculatedColumns.map((col, index) => (
                  <div key={index} className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-blue-800 dark:text-blue-300">{col.name}</span>
                          <span className="px-2 py-0.5 text-xs bg-blue-100 dark:bg-blue-800 text-blue-600 dark:text-blue-400 rounded">
                            {col.dataType}
                          </span>
                        </div>
                        <code className="text-xs text-gray-600 dark:text-gray-400 block mt-1 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
                          {col.expression}
                        </code>
                        {col.description && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{col.description}</p>
                        )}
                      </div>
                      <button
                        onClick={() => handleRemoveCalculatedColumn(index)}
                        className="text-red-500 hover:text-red-700 p-1"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {calculatedColumns.length === 0 && !showAddCalcColumn && (
              <div className="text-center py-4 text-gray-500 dark:text-gray-400 text-sm border border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
                No calculated columns added yet.
              </div>
            )}

            {/* Add calculated column form */}
            {showAddCalcColumn && (
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 space-y-3 border border-gray-200 dark:border-gray-600">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Column Name</label>
                    <input
                      type="text"
                      value={newCalcColumn.name}
                      onChange={(e) => setNewCalcColumn(prev => ({ ...prev, name: e.target.value.replace(/[^a-zA-Z0-9_]/g, '_') }))}
                      placeholder="e.g., total_revenue"
                      className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm dark:bg-gray-700"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Data Type</label>
                    <select
                      value={newCalcColumn.dataType}
                      onChange={(e) => setNewCalcColumn(prev => ({ ...prev, dataType: e.target.value }))}
                      className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm dark:bg-gray-700"
                    >
                      {DATA_TYPES.map(dt => (
                        <option key={dt.value} value={dt.value}>{dt.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">SQL Expression</label>
                  <textarea
                    value={newCalcColumn.expression}
                    onChange={(e) => setNewCalcColumn(prev => ({ ...prev, expression: e.target.value }))}
                    placeholder="e.g., price * quantity"
                    rows={2}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm font-mono dark:bg-gray-700"
                  />
                  <div className="flex flex-wrap gap-1 mt-1">
                    {EXPRESSION_EXAMPLES.map(ex => (
                      <button
                        key={ex.label}
                        type="button"
                        onClick={() => setNewCalcColumn(prev => ({ ...prev, expression: ex.example }))}
                        className="px-2 py-0.5 text-xs bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 rounded"
                      >
                        {ex.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Description (optional)</label>
                  <input
                    type="text"
                    value={newCalcColumn.description}
                    onChange={(e) => setNewCalcColumn(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="e.g., Total revenue from order"
                    className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm dark:bg-gray-700"
                  />
                </div>

                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => {
                      setShowAddCalcColumn(false);
                      setNewCalcColumn({ name: '', expression: '', description: '', dataType: 'FLOAT' });
                    }}
                    className="px-3 py-1.5 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600 text-sm rounded"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddCalculatedColumn}
                    disabled={!newCalcColumn.name || !newCalcColumn.expression}
                    className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white text-sm rounded"
                  >
                    Add
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-between sticky bottom-0 bg-white dark:bg-gray-800">
          {creating ? (
            <div className="flex items-center gap-3 text-purple-600 dark:text-purple-400">
              <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span className="text-sm font-medium">{createProgress}</span>
            </div>
          ) : (
            <div></div>
          )}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={creating}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg"
            >
              Cancel
            </button>
            <button
              onClick={handleCreate}
              disabled={!name || !baseTable || (relationships.length === 0 && customJoins.length === 0) || creating}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg flex items-center gap-2"
            >
              {creating ? (
                <>
                  <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Creating...
                </>
              ) : (
                `Create View (${getTotalSelectedColumns()} columns)`
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Create Union View Modal Component
const CreateUnionViewModal = ({ dataSources, onClose, onCreate }) => {
  const [name, setName] = useState('');
  const [selectedTables, setSelectedTables] = useState([]);
  const [addSourceLabel, setAddSourceLabel] = useState(true);
  const [removeDuplicates, setRemoveDuplicates] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createProgress, setCreateProgress] = useState('');

  const readyDataSources = dataSources.filter(ds => ds.status === 'ready' && ds.bigQueryTable);

  const handleToggleTable = (bigQueryTable) => {
    setSelectedTables(prev =>
      prev.includes(bigQueryTable)
        ? prev.filter(t => t !== bigQueryTable)
        : [...prev, bigQueryTable]
    );
  };

  const handleCreate = async () => {
    if (selectedTables.length < 2) return;
    setCreating(true);
    setCreateProgress('Creating union view...');
    try {
      setCreateProgress('Deploying to BigQuery...');
      await onCreate({
        name,
        baseTable: selectedTables[0],
        unionTables: selectedTables.slice(1),
        sourceLabel: addSourceLabel ? 'source_table' : null,
        unionDistinct: removeDuplicates
      });
      setCreateProgress('Complete!');
    } catch (error) {
      setCreateProgress('');
      setCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-800">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Create Union View</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Stack tables with the same structure into a single view using UNION ALL.
          </p>
        </div>

        <div className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">View Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., all_sales_combined"
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 dark:bg-gray-700"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Select Tables to Stack ({selectedTables.length} selected)
            </label>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
              Select at least 2 tables with the same or similar columns. They will be combined vertically.
            </p>
            <div className="space-y-2 max-h-64 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg p-3">
              {readyDataSources.map((ds) => (
                <label
                  key={ds._id}
                  className={`flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition-colors ${
                    selectedTables.includes(ds.bigQueryTable)
                      ? 'bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800'
                      : 'hover:bg-gray-50 dark:hover:bg-gray-700 border border-transparent'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedTables.includes(ds.bigQueryTable)}
                    onChange={() => handleToggleTable(ds.bigQueryTable)}
                    className="rounded text-indigo-600"
                  />
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-medium text-gray-900 dark:text-white block truncate">{ds.name}</span>
                    <span className="text-xs text-gray-500 dark:text-gray-400 block truncate">{ds.bigQueryTable}</span>
                  </div>
                  <span className="text-xs text-gray-400">
                    {ds.schemaInfo?.tables?.[0]?.columns?.length || '?'} cols
                  </span>
                </label>
              ))}
              {readyDataSources.length === 0 && (
                <div className="text-center py-4 text-gray-500 text-sm">No data sources available.</div>
              )}
            </div>
          </div>

          <div className="border-t border-gray-200 dark:border-gray-700 pt-4 space-y-3">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={addSourceLabel}
                onChange={(e) => setAddSourceLabel(e.target.checked)}
                className="rounded text-indigo-600"
              />
              <div>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Add source label column</span>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Adds a "source_table" column so you can tell which original table each row came from
                </p>
              </div>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={removeDuplicates}
                onChange={(e) => setRemoveDuplicates(e.target.checked)}
                className="rounded text-indigo-600"
              />
              <div>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Remove duplicate rows</span>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Uses UNION DISTINCT instead of UNION ALL to eliminate exact duplicate rows
                </p>
              </div>
            </label>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-between sticky bottom-0 bg-white dark:bg-gray-800">
          {creating ? (
            <div className="flex items-center gap-3 text-indigo-600 dark:text-indigo-400">
              <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span className="text-sm font-medium">{createProgress}</span>
            </div>
          ) : (
            <div></div>
          )}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={creating}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 rounded-lg"
            >
              Cancel
            </button>
            <button
              onClick={handleCreate}
              disabled={!name || selectedTables.length < 2 || creating}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg flex items-center gap-2"
            >
              {creating ? (
                <>
                  <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Creating...
                </>
              ) : (
                `Create Union View (${selectedTables.length} tables)`
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Edit View Modal Component
const EditViewModal = ({ view, relationships, dataSources, onClose, onSave }) => {
  const [calculatedColumns, setCalculatedColumns] = useState(view.calculatedColumns || []);
  const [saving, setSaving] = useState(false);
  const [saveProgress, setSaveProgress] = useState('');
  const [joinTypes, setJoinTypes] = useState(() => {
    const types = {};
    (view.includedTables || []).forEach(t => {
      const key = t.relationshipId || `custom:${t.table}`;
      types[key] = t.joinType || 'LEFT';
    });
    return types;
  });

  // Editable custom join conditions, keyed by table name
  const [customJoinConditions, setCustomJoinConditions] = useState(() => {
    const conditions = {};
    (view.includedTables || []).forEach(t => {
      if (t.customJoinCondition) {
        conditions[t.table] = t.customJoinCondition;
      }
    });
    return conditions;
  });
  
  // Handle save with loading state
  const handleSave = async () => {
    setSaving(true);
    setSaveProgress('Saving changes...');
    
    try {
      setSaveProgress('Deploying view...');
      // Build separate joinTypes for relationship-based vs custom joins
      const relJoinTypes = {};
      const customJoinUpdates = [];
      for (const [key, jt] of Object.entries(joinTypes)) {
        if (key.startsWith('custom:')) {
          const table = key.replace('custom:', '');
          customJoinUpdates.push({
            table,
            joinType: jt,
            condition: customJoinConditions[table] || ''
          });
        } else {
          relJoinTypes[key] = jt;
        }
      }
      await onSave({ 
        calculatedColumns, 
        joinTypes: relJoinTypes,
        customJoinUpdates: customJoinUpdates.length > 0 ? customJoinUpdates : undefined,
        selectedColumns: buildSelectedColumnsArray()
      });
      setSaveProgress('Complete!');
    } catch (error) {
      setSaveProgress('');
      setSaving(false);
      throw error;
    }
  };
  
  // Get all tables in this view
  const allTables = [view.baseTable, ...(view.includedTables || []).map(t => t.table)];
  
  // Get columns for a table from dataSources
  const getColumnsForTable = (tableName) => {
    const ds = dataSources.find(d => d.bigQueryTable === tableName);
    return ds?.schemaInfo?.tables?.[0]?.columns || [];
  };
  
  // Initialize selectedColumns from existing view.selectedColumns
  const [selectedColumns, setSelectedColumns] = useState(() => {
    const initial = {};
    allTables.forEach(t => {
      initial[t] = [];
    });
    if (view.selectedColumns && view.selectedColumns.length > 0) {
      // Restore saved selection, expanding wildcards back to full column list
      view.selectedColumns.forEach(sc => {
        const tableName = sc.source === 'base' ? view.baseTable : sc.source;
        if (!initial[tableName]) return;
        if (sc.column === '*') {
          initial[tableName] = getColumnsForTable(tableName).map(c => c.name);
        } else {
          initial[tableName].push(sc.column);
        }
      });
    } else {
      // No saved selection: default ALL tables to all columns (matches SQL generator behavior)
      allTables.forEach(t => {
        initial[t] = getColumnsForTable(t).map(c => c.name);
      });
    }
    return initial;
  });
  
  const [selectAllTables, setSelectAllTables] = useState(() => {
    const initial = {};
    allTables.forEach(t => {
      const allCols = getColumnsForTable(t).map(c => c.name);
      initial[t] = selectedColumns[t]?.length === allCols.length;
    });
    return initial;
  });
  
  const [showAddCalcColumn, setShowAddCalcColumn] = useState(false);
  const [columnSearchTerm, setColumnSearchTerm] = useState('');
  const [newCalcColumn, setNewCalcColumn] = useState({
    name: '',
    expression: '',
    description: '',
    dataType: 'FLOAT'
  });

  // Toggle all columns for a table
  const handleToggleAllColumns = (tableName, checked) => {
    setSelectAllTables(prev => ({ ...prev, [tableName]: checked }));
    if (checked) {
      const allCols = getColumnsForTable(tableName).map(c => c.name);
      setSelectedColumns(prev => ({ ...prev, [tableName]: allCols }));
    } else {
      setSelectedColumns(prev => ({ ...prev, [tableName]: [] }));
    }
  };

  // Toggle individual column
  const handleToggleColumn = (tableName, columnName, checked) => {
    setSelectedColumns(prev => {
      const current = prev[tableName] || [];
      if (checked) {
        return { ...prev, [tableName]: [...current, columnName] };
      } else {
        return { ...prev, [tableName]: current.filter(c => c !== columnName) };
      }
    });
  };

  // Build selectedColumns array for API
  // Uses wildcard for a fully-selected table so backend can COALESCE same-named columns
  const buildSelectedColumnsArray = () => {
    const result = [];
    for (const [tableName, columns] of Object.entries(selectedColumns)) {
      if (columns.length === 0) continue;
      const source = tableName === view.baseTable ? 'base' : tableName;
      const allCols = getColumnsForTable(tableName).map(c => c.name);
      if (columns.length === allCols.length) {
        result.push({ source, column: '*', alias: null });
      } else {
        for (const col of columns) {
          result.push({ source, column: col, alias: null });
        }
      }
    }
    return result;
  };

  // Count total selected columns
  const getTotalSelectedColumns = () => {
    return Object.values(selectedColumns).reduce((sum, cols) => sum + cols.length, 0);
  };

  const handleAddCalculatedColumn = () => {
    if (!newCalcColumn.name || !newCalcColumn.expression) return;
    setCalculatedColumns(prev => [...prev, { ...newCalcColumn }]);
    setNewCalcColumn({ name: '', expression: '', description: '', dataType: 'FLOAT' });
    setShowAddCalcColumn(false);
  };

  const handleRemoveCalculatedColumn = (index) => {
    setCalculatedColumns(prev => prev.filter((_, i) => i !== index));
  };

  const getJoinKey = (included) => included.relationshipId || `custom:${included.table}`;

  const handleJoinTypeChange = (key, newType) => {
    setJoinTypes(prev => ({ ...prev, [key]: newType }));
  };

  // Get relationship details for included tables
  const getRelationshipForTable = (included) => {
    return relationships.find(r => r.id === included.relationshipId);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-800">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Edit View: {view.name}</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Modify calculated columns and join types.
          </p>
        </div>
        
        <div className="p-6 space-y-6">
          {/* View Info (Read-only) */}
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500 dark:text-gray-400">Name:</span>
                <span className="ml-2 font-medium text-gray-900 dark:text-white">{view.name}</span>
              </div>
              <div>
                <span className="text-gray-500 dark:text-gray-400">Status:</span>
                <span className={`ml-2 px-2 py-0.5 text-xs rounded ${
                  view.status === 'ready' 
                    ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                    : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300'
                }`}>
                  {view.status}
                </span>
              </div>
              <div className="col-span-2">
                <span className="text-gray-500 dark:text-gray-400">Base Table:</span>
                <span className="ml-2 font-medium text-gray-900 dark:text-white">{view.baseTable}</span>
              </div>
              {view.bigQueryViewName && (
                <div className="col-span-2">
                  <span className="text-gray-500 dark:text-gray-400">BigQuery View:</span>
                  <span className="ml-2 font-mono text-sm text-purple-600 dark:text-purple-400">{view.bigQueryViewName}</span>
                </div>
              )}
            </div>
          </div>

          {/* Included Tables with Join Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Included Tables & Join Types</label>
            <div className="space-y-3">
              {(view.includedTables || []).map((included, index) => {
                const rel = getRelationshipForTable(included);
                const isCustom = !!included.customJoinCondition;
                const key = getJoinKey(included);
                return (
                  <div key={index} className={`rounded-lg overflow-hidden border ${isCustom ? 'border-green-200 dark:border-green-800' : 'border-gray-200 dark:border-gray-700'}`}>
                    <div className={`flex items-center justify-between px-3 py-2 ${isCustom ? 'bg-green-50 dark:bg-green-900/20' : 'bg-gray-50 dark:bg-gray-700'}`}>
                      <div className="flex items-center gap-2 text-sm">
                        {isCustom ? (
                          <span className="px-2 py-0.5 text-xs bg-green-100 dark:bg-green-800 text-green-700 dark:text-green-300 rounded">CUSTOM</span>
                        ) : (
                          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                          </svg>
                        )}
                        <span className="font-medium text-gray-900 dark:text-white">{included.table}</span>
                        {rel && (
                          <span className="text-gray-500 dark:text-gray-400 text-xs">
                            via {rel.sourceColumn} → {rel.targetColumn}
                          </span>
                        )}
                      </div>
                      <select
                        value={joinTypes[key] || 'LEFT'}
                        onChange={(e) => handleJoinTypeChange(key, e.target.value)}
                        className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm dark:bg-gray-700"
                      >
                        <option value="LEFT">LEFT JOIN</option>
                        <option value="INNER">INNER JOIN</option>
                        <option value="RIGHT">RIGHT JOIN</option>
                        <option value="FULL">FULL JOIN</option>
                      </select>
                    </div>
                    {isCustom && (
                      <div className="px-3 py-2 bg-white dark:bg-gray-800">
                        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                          SQL ON Condition
                        </label>
                        <textarea
                          value={customJoinConditions[included.table] || ''}
                          onChange={(e) => setCustomJoinConditions(prev => ({ ...prev, [included.table]: e.target.value }))}
                          rows={2}
                          className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm font-mono dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                        <p className="text-xs text-gray-400 mt-1">
                          Use <code className="bg-gray-100 dark:bg-gray-600 px-1 rounded">base.</code> for base table, <code className="bg-gray-100 dark:bg-gray-600 px-1 rounded">join_table.</code> for this table.
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Column Selection Section */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Select Columns</label>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Choose which columns to include ({getTotalSelectedColumns()} selected)
                </p>
              </div>
            </div>

            {/* Column Search */}
            <div className="relative mb-3">
              <svg className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                value={columnSearchTerm}
                onChange={(e) => setColumnSearchTerm(e.target.value)}
                placeholder="Search columns..."
                className="w-full pl-9 pr-8 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {columnSearchTerm && (
                <button
                  onClick={() => setColumnSearchTerm('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              )}
            </div>

            <div className="space-y-4 max-h-64 overflow-y-auto">
              {/* Base Table Columns */}
              {getColumnsForTable(view.baseTable).filter(col => col.name.toLowerCase().includes(columnSearchTerm.toLowerCase())).length > 0 && (
              <div className="border border-purple-200 dark:border-purple-800 rounded-lg overflow-hidden">
                <div className="bg-purple-50 dark:bg-purple-900/30 px-3 py-2 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-0.5 text-xs bg-purple-200 dark:bg-purple-800 text-purple-700 dark:text-purple-300 rounded">BASE</span>
                    <span className="font-medium text-gray-900 dark:text-white text-sm">{view.baseTable}</span>
                    <span className="text-xs text-gray-500">
                      ({(selectedColumns[view.baseTable] || []).length}/{getColumnsForTable(view.baseTable).length})
                    </span>
                  </div>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={selectAllTables[view.baseTable] || false}
                      onChange={(e) => handleToggleAllColumns(view.baseTable, e.target.checked)}
                      className="rounded text-purple-600"
                    />
                    <span className="text-gray-600 dark:text-gray-400">All</span>
                  </label>
                </div>
                <div className="p-3 grid grid-cols-2 md:grid-cols-3 gap-2 max-h-40 overflow-y-auto bg-white dark:bg-gray-800">
                  {getColumnsForTable(view.baseTable)
                    .filter(col => col.name.toLowerCase().includes(columnSearchTerm.toLowerCase()))
                    .map((col) => (
                    <label key={col.name} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 px-2 py-1 rounded">
                      <input
                        type="checkbox"
                        checked={(selectedColumns[view.baseTable] || []).includes(col.name)}
                        onChange={(e) => handleToggleColumn(view.baseTable, col.name, e.target.checked)}
                        className="rounded text-purple-600"
                      />
                      <span className="text-gray-700 dark:text-gray-300 truncate" title={`${col.name} (${col.type})`}>
                        {col.name}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
              )}

              {/* Joined Tables Columns */}
              {(view.includedTables || []).map((included) => {
                const filteredCols = getColumnsForTable(included.table).filter(col => col.name.toLowerCase().includes(columnSearchTerm.toLowerCase()));
                if (filteredCols.length === 0) return null;
                const selectedCount = (selectedColumns[included.table] || []).length;
                return (
                <div key={included.table} className={`border rounded-lg overflow-hidden ${selectedCount === 0 ? 'border-amber-300 dark:border-amber-700' : 'border-gray-200 dark:border-gray-700'}`}>
                  <div className="bg-gray-50 dark:bg-gray-700 px-3 py-2 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 text-xs bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded">JOIN</span>
                      <span className="font-medium text-gray-900 dark:text-white text-sm">{included.table}</span>
                      <span className={`text-xs ${selectedCount === 0 ? 'text-amber-500 dark:text-amber-400 font-medium' : 'text-gray-500'}`}>
                        {selectedCount === 0 ? 'none selected — pick columns below' : `${selectedCount}/${getColumnsForTable(included.table).length}`}
                      </span>
                    </div>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={selectAllTables[included.table] || false}
                        onChange={(e) => handleToggleAllColumns(included.table, e.target.checked)}
                        className="rounded text-blue-600"
                      />
                      <span className="text-gray-600 dark:text-gray-400">All</span>
                    </label>
                  </div>
                  <div className="p-3 grid grid-cols-2 md:grid-cols-3 gap-2 max-h-40 overflow-y-auto bg-white dark:bg-gray-800">
                    {filteredCols.map((col) => (
                      <label key={col.name} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 px-2 py-1 rounded">
                        <input
                          type="checkbox"
                          checked={(selectedColumns[included.table] || []).includes(col.name)}
                          onChange={(e) => handleToggleColumn(included.table, col.name, e.target.checked)}
                          className="rounded text-blue-600"
                        />
                        <span className="text-gray-700 dark:text-gray-300 truncate" title={`${col.name} (${col.type})`}>
                          {col.name}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
                );
              })}

              {/* No results message */}
              {columnSearchTerm && allTables.every(t => getColumnsForTable(t).filter(col => col.name.toLowerCase().includes(columnSearchTerm.toLowerCase())).length === 0) && (
                <div className="text-center py-4 text-gray-500 text-sm">
                  No columns match "{columnSearchTerm}"
                </div>
              )}
            </div>
          </div>

          {/* Calculated Columns Section */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Calculated Columns</label>
                <p className="text-xs text-gray-500 dark:text-gray-400">Add or remove derived columns using SQL expressions</p>
              </div>
              <button
                onClick={() => setShowAddCalcColumn(true)}
                className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg flex items-center gap-1"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Column
              </button>
            </div>

            {/* List of calculated columns */}
            {calculatedColumns.length > 0 && (
              <div className="space-y-2 mb-4">
                {calculatedColumns.map((col, index) => (
                  <div key={index} className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-blue-800 dark:text-blue-300">{col.name}</span>
                          <span className="px-2 py-0.5 text-xs bg-blue-100 dark:bg-blue-800 text-blue-600 dark:text-blue-400 rounded">
                            {col.dataType}
                          </span>
                        </div>
                        <code className="text-xs text-gray-600 dark:text-gray-400 block mt-1 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
                          {col.expression}
                        </code>
                        {col.description && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{col.description}</p>
                        )}
                      </div>
                      <button
                        onClick={() => handleRemoveCalculatedColumn(index)}
                        className="text-red-500 hover:text-red-700 p-1"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {calculatedColumns.length === 0 && !showAddCalcColumn && (
              <div className="text-center py-4 text-gray-500 dark:text-gray-400 text-sm border border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
                No calculated columns.
              </div>
            )}

            {/* Add calculated column form */}
            {showAddCalcColumn && (
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 space-y-3 border border-gray-200 dark:border-gray-600">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Column Name</label>
                    <input
                      type="text"
                      value={newCalcColumn.name}
                      onChange={(e) => setNewCalcColumn(prev => ({ ...prev, name: e.target.value.replace(/[^a-zA-Z0-9_]/g, '_') }))}
                      placeholder="e.g., total_revenue"
                      className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm dark:bg-gray-700"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Data Type</label>
                    <select
                      value={newCalcColumn.dataType}
                      onChange={(e) => setNewCalcColumn(prev => ({ ...prev, dataType: e.target.value }))}
                      className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm dark:bg-gray-700"
                    >
                      {DATA_TYPES.map(dt => (
                        <option key={dt.value} value={dt.value}>{dt.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">SQL Expression</label>
                  <textarea
                    value={newCalcColumn.expression}
                    onChange={(e) => setNewCalcColumn(prev => ({ ...prev, expression: e.target.value }))}
                    placeholder="e.g., price * quantity"
                    rows={2}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm font-mono dark:bg-gray-700"
                  />
                  <div className="flex flex-wrap gap-1 mt-1">
                    {EXPRESSION_EXAMPLES.map(ex => (
                      <button
                        key={ex.label}
                        type="button"
                        onClick={() => setNewCalcColumn(prev => ({ ...prev, expression: ex.example }))}
                        className="px-2 py-0.5 text-xs bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 rounded"
                      >
                        {ex.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Description (optional)</label>
                  <input
                    type="text"
                    value={newCalcColumn.description}
                    onChange={(e) => setNewCalcColumn(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="e.g., Total revenue from order"
                    className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm dark:bg-gray-700"
                  />
                </div>

                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => {
                      setShowAddCalcColumn(false);
                      setNewCalcColumn({ name: '', expression: '', description: '', dataType: 'FLOAT' });
                    }}
                    className="px-3 py-1.5 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600 text-sm rounded"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddCalculatedColumn}
                    disabled={!newCalcColumn.name || !newCalcColumn.expression}
                    className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white text-sm rounded"
                  >
                    Add
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-between sticky bottom-0 bg-white dark:bg-gray-800">
          {saving ? (
            <div className="flex items-center gap-3 text-purple-600 dark:text-purple-400">
              <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span className="text-sm font-medium">{saveProgress}</span>
            </div>
          ) : (
            <p className="text-xs text-gray-500 dark:text-gray-400 self-center">
              Changes will require regenerating dbt models.
            </p>
          )}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={getTotalSelectedColumns() === 0 || saving}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg flex items-center gap-2"
            >
              {saving ? (
                <>
                  <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Saving...
                </>
              ) : (
                `Save Changes (${getTotalSelectedColumns()} columns)`
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Compact Stat Card Component
function CompactStatCard({ label, value, icon: Icon, color, loading }) {
  const colorClasses = {
    blue: 'bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800',
    green: 'bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-800',
    purple: 'bg-purple-50 dark:bg-purple-900/30 border-purple-200 dark:border-purple-800'
  };
  
  const iconColorClasses = {
    blue: 'text-blue-500',
    green: 'text-green-500',
    purple: 'text-purple-500'
  };
  
  return (
    <div className={`p-4 rounded-xl border ${colorClasses[color]} bg-white dark:bg-gray-800 shadow-sm`}>
      <div className="flex items-center gap-3">
        <Icon className={`w-6 h-6 ${iconColorClasses[color]}`} />
        <div>
          <div className="text-xl font-bold text-gray-900 dark:text-white">
            {loading ? '...' : value}
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400">{label}</div>
        </div>
      </div>
    </div>
  );
}

// Message Bubble Component for AI Chat
function MessageBubble({ message }) {
  const isUser = message.role === 'user';
  
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[85%] px-4 py-3 rounded-2xl ${
        isUser
          ? 'bg-purple-600 text-white rounded-br-sm'
          : message.isError
            ? 'bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-200 rounded-bl-sm'
            : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white rounded-bl-sm'
      }`}>
        {isUser ? (
          <p className="whitespace-pre-wrap">{message.content}</p>
        ) : (
          <div className="prose prose-sm dark:prose-invert max-w-none">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {message.content}
            </ReactMarkdown>
          </div>
        )}
        
        {/* Show tool calls if any */}
        {message.toolCalls?.length > 0 && (
          <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Actions performed:</p>
            <div className="space-y-1">
              {message.toolCalls.map((tc, idx) => (
                <div key={idx} className="flex items-center gap-2 text-xs">
                  <CheckCircleIcon className="w-4 h-4 text-green-500" />
                  <span className="text-gray-600 dark:text-gray-400">
                    {tc.name.replace(/_/g, ' ')}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default RelationshipEditor;

